import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * DestroyRefPolyfill
 *
 * Angular 15 equivalent for Angular 16+ DestroyRef.
 * Provides lifecycle management using Subject-based pattern.
 *
 * Usage in components:
 * ```typescript
 * export class MyComponent implements OnDestroy {
 *   private destroyRef = new DestroyRefPolyfill();
 *
 *   ngOnInit() {
 *     this.someObservable$
 *       .pipe(takeUntil(this.destroyRef.destroyed$))
 *       .subscribe();
 *   }
 *
 *   ngOnDestroy() {
 *     this.destroyRef.destroy();
 *   }
 * }
 * ```
 */
export class DestroyRefPolyfill {
  private readonly _destroy$ = new Subject<void>();
  private _destroyed = false;

  /**
   * Register a callback to be invoked when the component is destroyed.
   * @param callback Function to call on destruction
   */
  onDestroy(callback: () => void): void {
    if (this._destroyed) {
      // If already destroyed, execute callback immediately
      callback();
      return;
    }

    this._destroy$.pipe(take(1)).subscribe(() => {
      callback();
    });
  }

  /**
   * Trigger destruction and complete the destroy$ Subject.
   * Should be called in ngOnDestroy lifecycle hook.
   */
  destroy(): void {
    if (this._destroyed) {
      return;
    }

    this._destroyed = true;
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Get the destruction observable.
   * Use with takeUntil operator for automatic subscription cleanup.
   */
  get destroyed$(): Observable<void> {
    return this._destroy$.asObservable();
  }

  /**
   * Check if the component has been destroyed.
   */
  get isDestroyed(): boolean {
    return this._destroyed;
  }
}
